"use client";

import { useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import { toast } from "sonner";
import { useAuthGateClose } from "@/lib/hooks/useAuthGateClose";
import { safeCapture } from "@/lib/analytics/safeCapture";

// Matches MarketingNav.tsx/QuizPageNav.tsx/RecommendationViewer.tsx (AGENTS.md
// convention) — keeps AuthModal + react-hook-form + zod + the auth Turnstile
// widget out of the base /quiz bundle for every ungated visitor.
const AuthModal = dynamic(
  () => import("@/components/auth/AuthModal").then((m) => m.AuthModal),
  { ssr: false },
);

/**
 * Gates a repeat anonymous quiz start behind login/signup.
 * Rendered by QuizWizard when useQuizWizardController's init() reports
 * `initState === "auth_required"` (a 403 `login_required` from
 * POST /v1/questionnaires — TRA-338, behind an env kill switch).
 *
 * Closable — `useAuthGateClose` sends a dismissing visitor back where they
 * came from (results page, marketing page) rather than trapping them here.
 * On success, `onSuccess` (the controller's `restartQuiz()`) re-runs init() —
 * now authenticated, it lands on a prefilled quiz at step 1.
 */
export function QuizAuthGate({ onSuccess }: { onSuccess: () => void }) {
  const router = useRouter();
  const handleClose = useAuthGateClose(() => {
    safeCapture("quiz_auth_gate_dismissed");
  });

  function handleSuccess(method: "signup" | "login") {
    safeCapture("quiz_auth_gate_converted", { method });
    toast.success("You're in. Your previous answers are ready to adjust", {
      duration: 10000,
      action: {
        label: "View past results",
        onClick: () => router.push("/users/my-results"),
      },
    });
    onSuccess();
  }

  return (
    <AuthModal
      open={true}
      onOpenChange={handleClose}
      onSuccess={handleSuccess}
      // Always-open gate: onOpenChange(false) means "the visitor dismissed the
      // gate" (handleClose above), not "close after success" — without this, a
      // successful signup/login would fire quiz_auth_gate_dismissed and
      // navigate away before quiz_auth_gate_converted/toast/restartQuiz() ever ran.
      closeOnSuccess={false}
      // Deliberately not wired to handleClose/quiz_auth_gate_dismissed: the
      // visitor is mid-funnel (needs a password reset to continue), not
      // abandoning the gate, so counting it as a dismissal would conflate the
      // two.
      onForgotPassword={() => router.push("/reset-password")}
      initialTab="signup"
      eyebrow="One more step"
      title={
        <>
          Create a free account to{" "}
          <span className="font-editorial font-normal italic text-primary">
            take the quiz again
          </span>
        </>
      }
      description="Keep every set of results, come back to them any time, and pick up where you left off: your previous answers are carried forward."
      signupSubmitLabel="Create account & continue"
      loginSubmitLabel="Log in & continue"
    />
  );
}
